import autoTable from 'jspdf-autotable';
import { jsPDF } from 'jspdf';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { FileOpener } from '@capacitor-community/file-opener';
import {
  addCoverPage, addEndPage, drawContentPageHeader, generateColumnWiseTable, generateEntryCards,
  formatDate, getValue, getBase64Image, NAVY
} from '../StudentDashboard/pdfGenerator';

// Same branded cover/header/footer/table/card primitives as the PG e-logbook
// PDF (frontend/src/components/StudentDashboard/pdfGenerator.js) — reused
// directly rather than re-implemented, so the UG export looks identical.
// This file only supplies the UG-specific data shaping + the two section
// shapes PG's generic "entries" model doesn't have: a fixed syllabus list
// (AETCOM/Pandemic/Foundation Course — always-27/7/7 items, no blank-card
// padding) and grouped parent/child logs (FAP families->visits, Internship
// postings->activities).

const LOG_STATUS_LABEL = { pending: 'Pending Review', approved: 'Certified', needs_revision: 'Needs Revision', rejected: 'Rejected' };
const REVIEW_STATUS_LABEL = { pending: 'Pending Review', approved: 'Approved', needs_revision: 'Needs Revision' };
const PROGRESS_STATUS_LABEL = { not_started: 'Not Started', in_progress: 'In Progress', completed: 'Completed' };
const REFLECTION_TYPE_LABEL = { weekly: 'Weekly', monthly: 'Monthly', case: 'Case Reflection' };
const REQUEST_STATUS_LABEL = { requested: 'Requested', scheduled: 'Scheduled', completed: 'Completed', cancelled: 'Cancelled' };

const sameId = (a, b) => {
  const av = a?._id || a;
  const bv = b?._id || b;
  return av != null && bv != null && av.toString() === bv.toString();
};

const facultyNames = (teacherIds) =>
  (teacherIds || []).map(t => `${t.profile?.firstName || ''} ${t.profile?.lastName || ''}`.trim()).filter(Boolean).join(', ');

// ─────────────────────────────────────────────
// Fixed syllabus list cards (AETCOM / Pandemic / Foundation Course) — same
// card look as PG's generateEntryCards, but no blank-card padding since
// these are closed, pre-defined curriculum items rather than open logs.
// ─────────────────────────────────────────────
const generateFixedModuleCards = (doc, sectionTitle, fields, items, studentName, courseName, collegeInfo) => {
  doc.addPage('a4', 'portrait');
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  let y = drawContentPageHeader(doc, sectionTitle, studentName, courseName, collegeInfo);

  const drawFooter = () => {
    const pageNumber = doc.internal.getCurrentPageInfo().pageNumber;
    const total = doc.internal.getNumberOfPages();
    doc.setFontSize(7);
    doc.setTextColor(100, 100, 100);
    doc.text(`Page ${pageNumber} of ${total}`, W / 2, H - 6, { align: 'center' });
  };

  items.forEach((item) => {
    if (y > H - 60) {
      doc.addPage('a4', 'portrait');
      y = drawContentPageHeader(doc, sectionTitle, studentName, courseName, collegeInfo);
    }

    doc.setFillColor(...NAVY);
    doc.rect(12, y, W - 24, 7, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.text(item.cardTitle, 15, y + 5);
    if (item.date) {
      doc.text(`Completed: ${formatDate(item.date)}`, W / 2 - 15, y + 5);
    }
    if (item.verifiedBy) {
      doc.text(`Verified by Faculty: ${item.verifiedBy}`, W - 85, y + 5);
    }
    y += 10;

    const fieldRows = fields.map(f => [f.label, getValue(item.data, f.key) || '']);

    autoTable(doc, {
      body: fieldRows,
      startY: y,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 2.5, lineColor: [0, 0, 0], lineWidth: 0.4, overflow: 'linebreak' },
      columnStyles: {
        0: { cellWidth: 50, fontStyle: 'bold', textColor: [70, 70, 70], fillColor: [248, 249, 250] },
        1: { cellWidth: 'auto', textColor: [20, 20, 20] },
      },
      margin: { top: 38, left: 12, right: 12 },
      didDrawPage: () => {
        drawContentPageHeader(doc, sectionTitle, studentName, courseName, collegeInfo);
        drawFooter();
      },
    });

    y = doc.lastAutoTable.finalY + 6;
  });
};

// Oldest-first by a given date accessor — matches PG's own ascending sort
// for modules that have a natural "date of activity" field.
const sortByDate = (items, dateFn) =>
  [...items].sort((a, b) => (new Date(dateFn(a)).getTime() || 0) - (new Date(dateFn(b)).getTime() || 0));

export const generateUGLogbookPDF = async (studentName, courseName, collegeInfo = {}, studentInfo = {}, data = {}) => {
  if (collegeInfo.logo) {
    collegeInfo.logoBase64 = await getBase64Image(collegeInfo.logo);
  }

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  // Page 1: Cover (identical layout to PG's)
  addCoverPage(doc, studentName, courseName, collegeInfo, studentInfo);

  // ── E-Logbook: one table per subject with ≥1 logged competency ──
  const subjects = data.subjects || [];
  const competencyLogs = data.competencyLogs || [];
  subjects.forEach((subject) => {
    const subjectLogs = sortByDate(
      competencyLogs.filter(l => sameId(l.subjectId, subject._id) && l.status === 'approved'),
      l => l.dateOfEncounter
    );
    if (subjectLogs.length === 0) return;

    const module = {
      name: `E-Logbook — ${subject.name} (${subject.code})`,
      fields: [
        { key: 'code', label: 'Code' },
        { key: 'competency', label: 'Competency' },
        { key: 'level', label: 'Level Achieved' },
        { key: 'setting', label: 'Setting' },
        { key: 'caseContext', label: 'Case Context' },
        { key: 'status', label: 'Status' }
      ]
    };
    const entries = subjectLogs.map(l => ({
      entries: [{ data: {
        code: l.competencyCode,
        competency: l.competencySnapshot?.text || '',
        level: l.levelAchieved,
        setting: l.settingType,
        caseContext: l.caseContext || '',
        status: LOG_STATUS_LABEL[l.status] || l.status
      } }],
      createdAt: l.dateOfEncounter,
      status: l.status,
      review: { reviewerName: l.review?.reviewerName }
    }));

    doc.addPage('a4', 'landscape');
    const startY = drawContentPageHeader(doc, module.name, studentName, courseName, collegeInfo);
    generateColumnWiseTable(doc, module, entries, startY, studentName, courseName, collegeInfo, true);
  });

  // ── Fixed syllabus lists: AETCOM / Pandemic Module / Foundation Course ──
  const addProgressSection = (sectionTitle, modules, extraField) => {
    if (!modules || modules.length === 0) return;
    const fields = [
      { key: 'status', label: 'Progress Status' },
      ...(extraField ? [extraField] : []),
      { key: 'activitiesCompleted', label: 'Activities Completed' },
      { key: 'reflectionNotes', label: 'Reflection Notes' },
      { key: 'reviewStatus', label: 'Review Status' },
      { key: 'feedback', label: 'Faculty Feedback' }
    ];
    const items = modules.map(m => ({
      cardTitle: `${m.number || m.id} — ${m.title}`,
      date: m.completedAt,
      verifiedBy: m.review?.status === 'approved' ? m.review.reviewerName : '',
      data: {
        status: PROGRESS_STATUS_LABEL[m.status] || m.status,
        attendancePercent: m.attendancePercent != null ? `${m.attendancePercent}%` : '',
        activitiesCompleted: m.activitiesCompleted || '',
        reflectionNotes: m.reflectionNotes || '',
        reviewStatus: REVIEW_STATUS_LABEL[m.review?.status] || 'Pending Review',
        feedback: m.review?.feedback || m.facultyFeedback || ''
      }
    }));
    generateFixedModuleCards(doc, sectionTitle, fields, items, studentName, courseName, collegeInfo);
  };

  addProgressSection('AETCOM', data.aetcomModules);
  addProgressSection('Pandemic Module', data.pandemicModules);
  addProgressSection('Foundation Course', data.foundationCourseModules, { key: 'attendancePercent', label: 'Attendance %' });

  // ── Open-ended student logs: reuse PG's card renderer, but with no blank-
  // card padding (see note below) ──
  const addEntryCardSection = (sectionTitle, fields, entries) => {
    if (!entries || entries.length === 0) return;
    const module = { name: sectionTitle, fields };
    doc.addPage('a4', 'portrait');
    const startY = drawContentPageHeader(doc, module.name, studentName, courseName, collegeInfo);
    // blankCardCount 0: unlike PG's paper-logbook convention, this is a
    // student's own export of their already-recorded work, not a form with
    // spare rows for future handwritten entries.
    generateEntryCards(doc, module, entries, startY, studentName, courseName, collegeInfo, true, 0);
  };

  // Only faculty-approved entries make it into the export — pending/
  // needs-revision/rejected items aren't yet a verified part of the record.
  const isApproved = (reviewStatus) => reviewStatus === 'approved';

  addEntryCardSection('Reflections', [
    { key: 'type', label: 'Type' },
    { key: 'period', label: 'Period' },
    { key: 'reflectionText', label: 'Reflection' },
    { key: 'learningOutcome', label: 'Learning Outcome' },
    { key: 'reviewStatus', label: 'Review Status' },
    { key: 'feedback', label: 'Faculty Feedback' }
  ], sortByDate((data.reflections || []).filter(r => isApproved(r.review?.status)), r => r.date).map(r => ({
    entries: [{ data: {
      type: REFLECTION_TYPE_LABEL[r.type] || r.type,
      period: r.period,
      reflectionText: r.reflectionText,
      learningOutcome: r.learningOutcome || '',
      reviewStatus: REVIEW_STATUS_LABEL[r.review?.status] || 'Pending Review',
      feedback: r.review?.feedback || r.facultyFeedback || ''
    } }],
    createdAt: r.date,
    status: r.review?.status,
    review: { reviewerName: r.review?.reviewerName }
  })));

  addEntryCardSection('Electives', [
    { key: 'department', label: 'Department' },
    { key: 'duration', label: 'Duration' },
    { key: 'activitiesPerformed', label: 'Activities Performed' },
    { key: 'learningOutcomes', label: 'Learning Outcomes' },
    { key: 'reviewStatus', label: 'Review Status' },
    { key: 'feedback', label: 'Supervisor Feedback' }
  ], sortByDate((data.electives || []).filter(e => isApproved(e.review?.status)), e => e.fromDate).map(e => ({
    entries: [{ data: {
      department: e.department,
      duration: `${formatDate(e.fromDate)} - ${formatDate(e.toDate)}`,
      activitiesPerformed: e.activitiesPerformed || '',
      learningOutcomes: e.learningOutcomes || '',
      reviewStatus: REVIEW_STATUS_LABEL[e.review?.status] || 'Pending Review',
      feedback: e.review?.feedback || e.supervisorFeedback || ''
    } }],
    createdAt: e.fromDate,
    status: e.review?.status,
    review: { reviewerName: e.review?.reviewerName }
  })));

  addEntryCardSection('Early Clinical Exposure', [
    { key: 'exposureType', label: 'Exposure Type' },
    { key: 'site', label: 'Department / Site' },
    { key: 'linkedSubject', label: 'Linked Subject' },
    { key: 'topic', label: 'Topic' },
    { key: 'learningPoints', label: 'Learning Points' },
    { key: 'reflection', label: 'Reflection' },
    { key: 'reviewStatus', label: 'Review Status' },
    { key: 'feedback', label: 'Faculty Feedback' }
  ], sortByDate((data.eceSessions || []).filter(s => isApproved(s.review?.status)), s => s.date).map(s => ({
    entries: [{ data: {
      exposureType: s.exposureType,
      site: s.departmentOrSite,
      linkedSubject: s.linkedSubject,
      topic: s.topic,
      learningPoints: s.learningPoints || '',
      reflection: s.reflection || '',
      reviewStatus: REVIEW_STATUS_LABEL[s.review?.status] || 'Pending Review',
      feedback: s.review?.feedback || s.facultyFeedback || ''
    } }],
    createdAt: s.date,
    status: s.review?.status,
    review: { reviewerName: s.review?.reviewerName }
  })));

  addEntryCardSection('Conference / Seminars', [
    { key: 'event', label: 'Event' },
    { key: 'mode', label: 'Mode' },
    { key: 'role', label: 'Role' },
    { key: 'organizer', label: 'Organizer' },
    { key: 'location', label: 'Location' },
    { key: 'topic', label: 'Topic / Theme' },
    { key: 'keyLearnings', label: 'Key Learnings' },
    { key: 'certificate', label: 'Certificate Received' },
    { key: 'reviewStatus', label: 'Review Status' },
    { key: 'feedback', label: 'Faculty Feedback' }
  ], sortByDate((data.conferenceEntries || []).filter(c => isApproved(c.review?.status)), c => c.startDate || c.createdAt).map(c => ({
    entries: [{ data: {
      event: `${c.title} (${c.eventType})`,
      mode: c.mode,
      role: c.role,
      organizer: c.organizer || '',
      location: c.location || '',
      topic: c.topic || '',
      keyLearnings: c.keyLearnings || '',
      certificate: c.certificateReceived ? 'Yes' : 'No',
      reviewStatus: REVIEW_STATUS_LABEL[c.review?.status] || 'Pending Review',
      feedback: c.review?.feedback || c.facultyFeedback || ''
    } }],
    createdAt: c.startDate || c.createdAt,
    status: c.review?.status,
    review: { reviewerName: c.review?.reviewerName }
  })));

  // ── FAP: one section per adopted family, listing that family's visits ──
  const activityFields = [
    { key: 'procedureOrCase', label: 'Procedure / Case' },
    { key: 'involvementLevel', label: 'Involvement Level' },
    { key: 'caseSummary', label: 'Case Summary' },
    { key: 'learningPoints', label: 'Learning Points' },
    { key: 'reviewStatus', label: 'Review Status' },
    { key: 'feedback', label: 'Faculty Feedback' }
  ];
  const visitFields = [
    { key: 'programYear', label: 'Program Year' },
    { key: 'purpose', label: 'Purpose' },
    { key: 'findings', label: 'Findings' },
    { key: 'healthEducationGiven', label: 'Health Education Given' },
    { key: 'referralMade', label: 'Referral Made' },
    { key: 'reflection', label: 'Reflection' },
    { key: 'reviewStatus', label: 'Review Status' },
    { key: 'feedback', label: 'Faculty Feedback' }
  ];

  (data.families || []).forEach((family) => {
    const visits = sortByDate(
      (data.familyVisits || []).filter(v => sameId(v.familyId, family._id) && isApproved(v.review?.status)),
      v => v.visitDate
    );
    if (visits.length === 0) return;
    addEntryCardSection(`FAP — ${family.familyCode} (${family.villageName})`, visitFields, visits.map(v => ({
      entries: [{ data: {
        programYear: v.programYear,
        purpose: v.purpose,
        findings: v.findings || '',
        healthEducationGiven: v.healthEducationGiven || '',
        referralMade: v.referralMade || '',
        reflection: v.reflection || '',
        reviewStatus: REVIEW_STATUS_LABEL[v.review?.status] || 'Pending Review',
        feedback: v.review?.feedback || v.facultyFeedback || ''
      } }],
      createdAt: v.visitDate,
      status: v.review?.status,
      review: { reviewerName: v.review?.reviewerName }
    })));
  });

  // ── Internship: one section per posting, listing that posting's activities ──
  (data.internshipPostings || []).forEach((posting) => {
    const activities = sortByDate(
      (data.internshipActivities || []).filter(a => sameId(a.postingId, posting._id) && isApproved(a.review?.status)),
      a => a.date
    );
    if (activities.length === 0) return;
    const label = `Internship — ${posting.department} (${formatDate(posting.fromDate)} - ${formatDate(posting.toDate)})`;
    addEntryCardSection(label, activityFields, activities.map(a => ({
      entries: [{ data: {
        procedureOrCase: a.procedureOrCase,
        involvementLevel: a.involvementLevel,
        caseSummary: a.caseSummary || '',
        learningPoints: a.learningPoints || '',
        reviewStatus: REVIEW_STATUS_LABEL[a.review?.status] || 'Pending Review',
        feedback: a.review?.feedback || a.facultyFeedback || ''
      } }],
      createdAt: a.date,
      status: a.review?.status,
      review: { reviewerName: a.review?.reviewerName }
    })));
  });

  // ── DOAP: observation requests + whatever assessment has been recorded ──
  addEntryCardSection('DOAP', [
    { key: 'procedure', label: 'Procedure' },
    { key: 'venue', label: 'Venue / Ward' },
    { key: 'facultyObservers', label: 'Faculty Observer(s)' },
    { key: 'status', label: 'Status' },
    { key: 'domainScores', label: 'Domain Scores' },
    { key: 'globalRating', label: 'Global Rating' },
    { key: 'comments', label: 'Faculty Comments' }
  ], sortByDate((data.doapSessions || []).filter(d => d.status === 'completed'), d => d.proposedDate || d.createdAt).map(d => ({
    entries: [{ data: {
      procedure: d.procedure,
      venue: d.venueWard || '',
      facultyObservers: facultyNames(d.selectedTeacherIds),
      status: REQUEST_STATUS_LABEL[d.status] || d.status,
      domainScores: (d.assessment?.domainScores || []).map(s => `${s.domain}: ${s.score}`).join('; '),
      globalRating: d.assessment?.globalRating != null ? String(d.assessment.globalRating) : '',
      comments: d.assessment?.facultyComments || ''
    } }],
    createdAt: d.proposedDate || d.createdAt,
    status: d.status === 'completed' ? 'approved' : 'pending',
    review: { reviewerName: d.assessment?.assessedByName }
  })));

  // ── Professionalism (FAP assessment) ──
  addEntryCardSection('Professionalism (Formative Assessment)', [
    { key: 'posting', label: 'Posting / Department' },
    { key: 'facultyAssessors', label: 'Faculty' },
    { key: 'status', label: 'Status' },
    { key: 'domainScores', label: 'Domain Scores' },
    { key: 'remarks', label: 'Faculty Remarks' }
  ], sortByDate((data.professionalismAssessments || []).filter(p => p.status === 'completed'), p => p.createdAt).map(p => ({
    entries: [{ data: {
      posting: p.postingDepartment,
      facultyAssessors: facultyNames(p.selectedTeacherIds),
      status: REQUEST_STATUS_LABEL[p.status] || p.status,
      domainScores: (p.assessment?.domainScores || []).map(s => `${s.domain}: ${s.score}`).join('; '),
      remarks: p.assessment?.facultyRemarks || ''
    } }],
    createdAt: p.createdAt,
    status: p.status === 'completed' ? 'approved' : 'pending',
    review: { reviewerName: p.assessment?.assessedByName }
  })));

  // Last page: back cover (identical layout to PG's)
  doc.addPage('a4', 'portrait');
  addEndPage(doc, collegeInfo);

  const fileName = `${studentName.replace(/\s+/g, '_')}_UG_Logbook_${new Date().getTime()}.pdf`;

  if (Capacitor.isNativePlatform()) {
    const base64Data = doc.output('datauristring').split(',')[1];
    const result = await Filesystem.writeFile({
      path: fileName,
      data: base64Data,
      directory: Directory.Documents,
      recursive: true,
    });
    try {
      await FileOpener.open({ filePath: result.uri, contentType: 'application/pdf' });
    } catch (e) {
      alert(`PDF saved successfully:\n${fileName}\n\nOpen it from your Files app.`);
    }
  } else {
    doc.save(fileName);
  }
};

export default generateUGLogbookPDF;
